"use client"

import type React from "react"
import { useState } from "react"
import { X, UploadCloud, FileText, FileSpreadsheet, FileType, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"

interface UploadBulkResultsModalProps {
  isOpen: boolean
  onClose: () => void
  onUpload: (file: File) => void
}

export function UploadBulkResultsModal({ isOpen, onClose, onUpload }: UploadBulkResultsModalProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  const acceptedFileTypes = [
    "text/csv",
    "application/pdf",
    "application/xml",
    "text/xml",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ]

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)

    const files = Array.from(e.dataTransfer.files)
    const validFile = files.find((file) => acceptedFileTypes.includes(file.type))

    if (validFile) {
      setSelectedFile(validFile)
    } else {
      alert("Please upload a valid file format (CSV, PDF, XML, or Excel)")
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) {
      const file = files[0]
      if (acceptedFileTypes.includes(file.type)) {
        setSelectedFile(file)
      } else {
        alert("Please upload a valid file format (CSV, PDF, XML, or Excel)")
      }
    }
  }

  const handleRemoveFile = () => {
    setSelectedFile(null)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!selectedFile) {
      alert("Please select a file to upload")
      return
    }

    onUpload(selectedFile)
    alert(`Successfully uploaded results for multiple students from ${selectedFile.name}!`)
    setSelectedFile(null)
    onClose()
  }

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split(".").pop()?.toLowerCase()
    if (extension === "csv" || extension === "xlsx" || extension === "xls") {
      return <FileSpreadsheet className="w-8 h-8 text-blue-600" />
    }
    if (extension === "pdf") {
      return <FileText className="w-8 h-8 text-red-500" />
    }
    if (extension === "xml") {
      return <FileType className="w-8 h-8 text-orange-500" />
    }
    return <FileText className="w-8 h-8 text-gray-500" />
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + " B"
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + " KB"
    return (bytes / (1024 * 1024)).toFixed(2) + " MB"
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50">
      {/* Blurry Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
        onClick={onClose}
      />
      
      {/* Modal Container */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200">
          {/* Modal Header */}
          <div className="bg-teal-600 px-6 py-4 flex justify-between items-center">
            <h3 className="text-lg leading-6 font-bold text-white">Upload Student Results</h3>
            <button 
              onClick={onClose} 
              className="text-white hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-white/50 rounded-full p-1 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Modal Content */}
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-6">
              <div className="mb-6">
                <p className="text-sm text-gray-600 mb-4">
                  Upload student results individually or in bulk using CSV, PDF, XML, or Excel files.
                </p>

                {/* Upload Area */}
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md transition-colors ${
                    isDragging
                      ? "border-teal-400 bg-teal-50"
                      : selectedFile
                        ? "border-teal-400"
                        : "border-gray-300 hover:border-teal-400"
                  } bg-gray-50`}
                >
                  <div className="space-y-1 text-center">
                    {!selectedFile ? (
                      <>
                        <UploadCloud className="mx-auto h-12 w-12 text-gray-400" />
                        <div className="flex text-sm text-gray-600">
                          <label
                            htmlFor="bulk-file-upload"
                            className="relative cursor-pointer rounded-md font-medium text-teal-600 hover:text-teal-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-teal-500"
                          >
                            <span>Upload a file</span>
                            <input
                              id="bulk-file-upload"
                              name="bulk-file-upload"
                              type="file"
                              className="sr-only"
                              accept=".csv,.pdf,.xml,.xls,.xlsx"
                              onChange={handleFileChange}
                            />
                          </label>
                          <p className="pl-1">or drag and drop</p>
                        </div>
                        <p className="text-xs text-gray-500">CSV, PDF, XML, Excel up to 50MB</p>
                      </>
                    ) : (
                      <div className="flex items-center justify-between p-4 bg-gray-100 rounded-lg">
                        <div className="flex items-center gap-3">
                          {getFileIcon(selectedFile.name)}
                          <div className="text-left">
                            <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                            <p className="text-xs text-gray-500">{formatFileSize(selectedFile.size)}</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={handleRemoveFile}
                          className="text-red-500 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 rounded-full p-1"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* File Format Information */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Supported File Formats:</h4>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li>• CSV (.csv) - For bulk student results upload</li>
                  <li>• Excel (.xlsx, .xls) - Microsoft Excel spreadsheets with results</li>
                  <li>• PDF (.pdf) - Individual student result sheets</li>
                  <li>• XML (.xml) - Structured results data</li>
                </ul>
                <p className="text-xs text-gray-600 mt-3 font-medium">
                  Tip: Use Excel or CSV files for bulk uploads with multiple students
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-3 border-t border-gray-200">
              <Button 
                type="button" 
                variant="outline" 
                onClick={onClose}
                className="border-gray-300 hover:bg-gray-100 text-gray-700"
              >
                Cancel
              </Button>
              {selectedFile && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleRemoveFile}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 hover:border-red-300"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete File
                </Button>
              )}
              <Button 
                type="submit" 
                className="bg-teal-600 hover:bg-teal-700 text-white focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
                disabled={!selectedFile}
              >
                Confirm Upload
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}